//** Manages chat-specific actions and state such as opening an image in the chat, used to openImage in authentication logic */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import logger from '../utils/logger';
import { ILanguageOption } from '../components/Chat/LanguageSelector';
import { IGlossarySpec } from '../utils/documentTranslation.service';
import { ITranslationMessage } from '../components/Chat/TranslationMode';

// Shape for an uploaded file used by data analysis / file upload tools
export interface UploadedFileMeta {
  name: string;
  size: number; // bytes
  type: string; // mime type
  lastModified: number; // epoch ms
  data: string; // text for csv OR base64 for excel/other
  key?: string; // unique per upload instance for React list rendering
  fileId?: string; // backend-assigned file identifier after successful upload
  status?: 'pending' | 'uploading' | 'uploaded' | 'error'; // granular upload lifecycle
  errorMessage?: string; // optional error details for UI retry flows
}

// Secure random string generator for session IDs
function generateSecureRandomString(length = 9) {
  if (typeof window !== "undefined" && window.crypto && window.crypto.getRandomValues) {
    // 5 bytes yields at least 9 base-36 chars (each byte can be up to 2 base-36 chars)
    const array = new Uint8Array(5);
    window.crypto.getRandomValues(array);
    // Convert bytes to hex, then to base-36
    return Array.from(array, b => b.toString(36).padStart(2, '0')).join('').substring(0, length);
  } else {
    // Fallback for non-browser environments / test environments
    console.warn("window.crypto unavailable; falling back to Math.random for session ID randomness.");
    return Math.random().toString(36).substring(2, 2 + length);
  }
}

export interface ChatState {
  openImageSrc: string | null;
  openImageType?: 'image' | 'pdf' | null; // Track the type of content being viewed
  openImageName?: string | null;
  openImageUseCustomPdfHeader?: boolean;
  openJiraCardHtml: string | null; // HTML content of Jira card to display in modal
  uploadedFiles: UploadedFileMeta[]; // currently attached files (max 2 for now)
  analysisResults: Array<{
    file_id: string;
    filename: string;
    status: string;
    file_type: string;
  }>; // results metadata returned from data analysis API
  sessionId: string; // persistent session id for all API calls in this window
  conversationStarted: boolean; // true after first user message sent
  sentImageCount?: number; // cumulative images sent in user messages (general chat)
  sentOtherFileCount?: number; // cumulative non-image files sent in user messages (general chat)
  translationHistory: ITranslationMessage[]; // store translation messages to track history and prevent duplicates
  targetLanguages: ILanguageOption[]; // selected target languages for translation
  lastTranslation: { fileNames: string[]; languageCodes: string[]; glossaryFiles?: string[] } | null; // track last translation to prevent duplicates
  translationQuotaDetails: {
    limit: number | undefined;
    quotaRemaining: number | undefined;
    quotaResetUtc: string | undefined;
  } | null;
  imageQuotaDetails: {
    count: number;
    limit: number;
    remaining?: number;
    resetUtc?: string;
  } | null;
  isTranslating?: boolean; // whether a translation operation is in progress
  // glossary files stored as an array of IGlossarySpec
  glossaryFiles: IGlossarySpec[];
  translationError?: string; // store translation error messages
}

const initialState: ChatState = {
  openImageSrc: null,
  openImageType: null,
  openImageName: null,
  openImageUseCustomPdfHeader: false,
  openJiraCardHtml: null,
  uploadedFiles: [],
  analysisResults: [],
  sessionId: '', // Will be generated on app init
  conversationStarted: false,
  sentImageCount: 0,
  sentOtherFileCount: 0,
  targetLanguages: [],
  lastTranslation: null,
  translationQuotaDetails: null,
  imageQuotaDetails: null,
  isTranslating: false,
  translationHistory: [] as ITranslationMessage[],
  translationError: undefined,
  glossaryFiles: [] as IGlossarySpec[],
};

// Clear any previous session ID when module loads (new tab or page refresh)
if (typeof window !== 'undefined') {
  try {
    window.sessionStorage.removeItem('nav_session_id');
  } catch {
    /* ignore */
  }
}

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    openImage(state, action: PayloadAction<string | { src: string; type?: 'image' | 'pdf'; name?: string; useCustomPdfHeader?: boolean }>) {
      if (typeof action.payload === 'string') {
        state.openImageSrc = action.payload;
        state.openImageType = 'image'; // Default to image for backward compatibility
        state.openImageName = null;
        state.openImageUseCustomPdfHeader = false;
      } else {
        state.openImageSrc = action.payload.src;
        state.openImageType = action.payload.type || 'image';
        state.openImageName = action.payload.name || null;
        state.openImageUseCustomPdfHeader = !!action.payload.useCustomPdfHeader;
      }
    },
    closeImage(state) {
      state.openImageSrc = null;
      state.openImageType = null;
      state.openImageName = null;
      state.openImageUseCustomPdfHeader = false;
    },
    openJiraCard(state, action: PayloadAction<string>) {
      state.openJiraCardHtml = action.payload;
    },
    closeJiraCard(state) {
      state.openJiraCardHtml = null;
    },
    setUploadedFile(state, action: PayloadAction<UploadedFileMeta | null>) { // backward compat (will set single file replacing list)
      if (action.payload) {
        state.uploadedFiles = [action.payload];
      } else {
        state.uploadedFiles = [];
      }
    },
    addUploadedFile(state, action: PayloadAction<UploadedFileMeta>) {
      // Previous hard cap of 2 removed to allow dynamic limits (2 general / 5 images-only / 2 analysis) enforced at UI layer.
      // Dedup by name + lastModified: guard against race conditions where the same file is
      // dispatched twice before the UI-level duplicate check sees the Redux update.
      const alreadyPresent = state.uploadedFiles.some(
        f => f.name === action.payload.name && f.lastModified === action.payload.lastModified
      );
      if (alreadyPresent) return;
      state.uploadedFiles.push(action.payload);
    },
    updateUploadedFile(state, action: PayloadAction<{ name: string; changes: Partial<UploadedFileMeta> }>) {
      const idx = state.uploadedFiles.findIndex(f => f.name === action.payload.name);
      if (idx !== -1) {
        state.uploadedFiles[idx] = { ...state.uploadedFiles[idx], ...action.payload.changes };
      }
    },
    removeUploadedFile(state, action: PayloadAction<string>) { // remove by name (assuming unique names per session)
      state.uploadedFiles = state.uploadedFiles.filter(f => f.name !== action.payload);
    },
    clearUploadedFiles(state) {
      state.uploadedFiles = [];
    },
    clearUploadedFile(state) { // legacy alias
      state.uploadedFiles = [];
    },
    setAnalysisResults(state, action: PayloadAction<ChatState['analysisResults']>) {
      state.analysisResults = action.payload || [];
    },
    clearAnalysisResults(state) {
      state.analysisResults = [];
    },
    ensureSessionId(state) {
      if (!state.sessionId) {
        // Generate a NEW unique session ID for each tab/session
        // Format: sess_timestamp_randomString
        const newId = `sess_${Date.now()}_${generateSecureRandomString(9)}`;
        state.sessionId = newId;
        // Store in sessionStorage for consistency within the same tab session
        // (sessionStorage is isolated per tab, unlike localStorage)
        try { 
          if (typeof window !== 'undefined') {
            window.sessionStorage.setItem('nav_session_id', newId);
            logger.log('[Session] New session ID generated:', newId);
          }
        } catch {/* ignore */}
      }
    },
    resetSessionId(state) {
      // Force generation of a new session ID (e.g., when returning to home)
      const newId = `sess_${Date.now()}_${generateSecureRandomString(9)}`;
      state.sessionId = newId;
      try { 
        if (typeof window !== 'undefined') {
          window.sessionStorage.setItem('nav_session_id', newId);
          logger.log('[Session] Session ID reset (home button clicked):', newId);
        }
      } catch {/* ignore */}
    },
    markConversationStarted(state) {
      state.conversationStarted = true;
    },
    incrementSentFileCounts(state, action: PayloadAction<{ images: number; nonImages: number }>) {
      if (typeof state.sentImageCount !== 'number') state.sentImageCount = 0;
      if (typeof state.sentOtherFileCount !== 'number') state.sentOtherFileCount = 0;
      state.sentImageCount += action.payload.images;
      state.sentOtherFileCount += action.payload.nonImages;
    },
    setTranslationHistory(state, action: PayloadAction<ITranslationMessage[]>) {
      state.translationHistory = action.payload;
    },
    // Append a single message to translationHistory without requiring the caller to
    // hold a reference to the current list — avoids the React stale-closure problem
    // that occurs when dispatching inside an async function after an await.
    addTranslationMessage(state, action: PayloadAction<ITranslationMessage>) {
      state.translationHistory = [...state.translationHistory, action.payload];
    },
    setTranslationError(state, action: PayloadAction<string>) {
      state.translationError = action.payload;
      if(action.payload) {
        state.isTranslating = false; // Stop any loading states if there's an error
      }
    },
    setTargetLanguages(state, action: PayloadAction<ILanguageOption[]>) {
      state.targetLanguages = action.payload;
    },
    clearTargetLanguages(state) {
      state.targetLanguages = [];
    },
    setLastTranslation(state, action: PayloadAction<{ fileNames: string[]; languageCodes: string[]; glossaryFiles?: string[] }>) {
      state.lastTranslation = action.payload;
    },
    clearTranslationData(state) {
      state.lastTranslation = null;
      state.translationQuotaDetails = null;
      state.targetLanguages = [];
      state.glossaryFiles = [] as IGlossarySpec[];
      state.translationHistory = [] as ITranslationMessage[];
      state.translationError = undefined;
      state.uploadedFiles = []; // Clear uploaded files to reset state for new translation session
    },
    setIsTranslating(state, action: PayloadAction<boolean>) {
      state.isTranslating = action.payload;
    },
    setTranslationQuota(state, action: PayloadAction<ChatState['translationQuotaDetails']>) {
      state.translationQuotaDetails = action.payload;
    },
    setImageQuota(state, action: PayloadAction<ChatState['imageQuotaDetails']>) {
      state.imageQuotaDetails = action.payload;
    },
    clearImageQuotaAlert(state) {
      state.imageQuotaDetails = null;
    },
    // Add or replace a glossary spec (IGlossarySpec) for a language
    addGlossaryFile(state, action: PayloadAction<IGlossarySpec>) {
      const spec = action.payload;
      const idx = state.glossaryFiles.findIndex(g => g.lang === spec.lang && g.file.filename === spec.file.filename);
      if (idx !== -1) {
        state.glossaryFiles[idx] = spec;
      } else {
        // If another glossary exists for same lang but different filename, replace it (keep one per lang)
        const existingIdx = state.glossaryFiles.findIndex(g => g.lang === spec.lang);
        if (existingIdx !== -1) state.glossaryFiles[existingIdx] = spec;
        else state.glossaryFiles.push(spec);
      }
    },
    removeGlossaryFile(state, action: PayloadAction<{ languageCode: string; name: string }>) {
      const { languageCode, name } = action.payload;
      state.glossaryFiles = state.glossaryFiles.filter(g => !(g.lang === languageCode && g.file.filename === name));
    },
    clearGlossaryForLanguage(state, action: PayloadAction<string>) {
      const lang = action.payload;
      state.glossaryFiles = state.glossaryFiles.filter(g => g.lang !== lang);
    }
  },
});

export const { openImage, closeImage, openJiraCard, closeJiraCard, setUploadedFile, addUploadedFile, updateUploadedFile, removeUploadedFile, clearUploadedFiles, clearUploadedFile, setAnalysisResults, clearAnalysisResults, ensureSessionId, resetSessionId, markConversationStarted, incrementSentFileCounts, setTargetLanguages, clearTargetLanguages, setLastTranslation, clearTranslationData, setIsTranslating, setTranslationQuota, setImageQuota, clearImageQuotaAlert, addGlossaryFile, removeGlossaryFile, clearGlossaryForLanguage, setTranslationHistory, addTranslationMessage, setTranslationError } = chatSlice.actions;
export default chatSlice.reducer;
